import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, RevealImage } from "@/lib/motion";
import { Page, PageHero, Overline, CTABand } from "@/components/shared";
import { useContent } from "@/lib/ContentProvider";

export default function Doctor() {
  const { doctors } = useContent();
  const [featured, ...others] = doctors;

  return (
    <Page testid="doctor-page">
      <PageHero
        overline="Our Doctors"
        titleLines={["The hands", <span key="2">behind your <span className="italic text-alch-terracotta">recovery</span></span>]}
        subtitle="Experienced surgeons and clinicians who have delivered thousands of successful procedures."
      />

      {featured && (
        <section className="px-5 py-24 md:px-8 md:py-32">
          <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem]">
                <RevealImage src={featured.image} alt={featured.name} className="h-[30rem]" />
                <div className="absolute inset-0 bg-gradient-to-t from-alch-forest/40 to-transparent" />
              </div>
            </Reveal>
            <div>
              <Overline>Chief Surgeon</Overline>
              <Reveal>
                <h2 className="mt-5 font-serif text-4xl text-alch-forest md:text-6xl">{featured.name}</h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-3 text-sm uppercase tracking-[0.14em] text-alch-terracotta">{featured.role}</p>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-6 leading-relaxed text-alch-ink/80">{featured.bio}</p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(featured.expertise || []).map((e) => (
                    <span key={e} className="rounded-full border border-alch-forest/20 px-4 py-1.5 text-xs text-alch-forest">{e}</span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <Link to={`/doctor/${featured.slug}`} data-testid={`doctor-view-${featured.slug}`} className="group mt-8 inline-flex items-center gap-2 rounded-full bg-alch-forest px-6 py-3.5 text-sm font-medium text-alch-bg transition-transform hover:scale-[1.04]">
                  View full profile
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="bg-alch-surface px-5 py-24 md:px-8 md:py-32">
          <div className="mx-auto max-w-7xl">
            <Overline>The team</Overline>
            <Reveal>
              <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight text-alch-forest md:text-5xl">
                Meet our specialists
              </h2>
            </Reveal>
            <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {others.map((d, i) => (
                <Reveal key={d.id || d.slug} delay={i * 0.08}>
                  <Link to={`/doctor/${d.slug}`} data-testid={`doctor-card-${d.slug}`} className="group block">
                    <div className="overflow-hidden rounded-[1.5rem]">
                      <RevealImage src={d.image} alt={d.name} className="h-72" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <h3 className="mt-5 font-serif text-2xl text-alch-forest">{d.name}</h3>
                    <p className="mt-1 text-sm uppercase tracking-[0.14em] text-alch-terracotta">{d.role}</p>
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-alch-ink/70">{d.bio}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </Page>
  );
}
